import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivationEnd, Router, RouterOutlet } from '@angular/router';
import { TealiumUtagService } from '@woolworthsnz/analytics';
import { WithDynamicContent } from '@woolworthsnz/dynamic-content';
import { FormComponent, FieldsetComponent } from '@woolworthsnz/form';
import {
	fulfilmentChangeCommon,
	FulfilmentService,
	FulfilmentState,
	FulfilmentStoreService,
	PickupType,
	FulfilmentMethodComponent,
	FulfilmentTimeSelectionComponent,
	FulfilmentDateSelectionComponent,
	FulfilmentFlexibleDeliverySlotSelectionComponent,
} from '@woolworthsnz/fulfilment';
import {
	BasketService,
	BasketState,
	EXPRESS_LANDING_PAGE_PATH,
	HAVE_YOU_FORGOTTEN_PATH,
	MODAL_ROUTES,
	ORDER_CONFIRMATION_PATH,
	REGISTRATION_PATH,
	REGISTRATION_SHOPPER_DETAILS_PATH,
	REGISTRATION_SUCCESSFUL_PATH,
	REVIEW_ORDER_PATH,
} from '@woolworthsnz/shop';
import {
	Alert,
	AppSettingsService,
	ButtonState,
	checkFullActivationEndForData,
	FeatureService,
	HistoryService,
	LoggingService,
	MedalliaSurveyDataPushService,
	ModalRoutingService,
	ShopperService,
	ShopperState,
	WithPageMeta,
	AlertComponent,
	ButtonComponent,
	SvgIconComponent,
} from '@woolworthsnz/styleguide';
import {
	BasketTotalsResponse,
	ContextResponse,
	ExpressFulfilmentAvailabilityStatusResponse,
	ExpressFulfilmentSettingsResponse,
	FulfilmentWindowSummarySlot,
	OptionalMaybeBoolean,
	MaybeString,
	SlotsOffering,
	UpdateTrolleyMetadataRequest,
} from '@woolworthsnz/trader-api';
import dayjs from 'dayjs';
import equal from 'fast-deep-equal';
import { distinctUntilChanged, filter, map, take, takeUntil } from 'rxjs/operators';
import {
	cartFlowCheckoutInitiated,
	cartFlowCommon,
	cartFlowFulfilmentSubmitted,
	fulfilmentChangeCompleted,
	fulfilmentChangeLoadTimeslots,
} from './how-where-when.constants';
import { DeliverySaverTagComponent } from '../../components/delivery-saver-tag/delivery-saver-tag.component';
import { FunnelBreadcrumbsComponent } from '../../../core/components/funnel-breadcrumbs/funnel-breadcrumbs.component';
import { NgIf, NgClass, CurrencyPipe } from '@angular/common';
import AvailabilityStatusEnum = ExpressFulfilmentAvailabilityStatusResponse.AvailabilityStatusEnum;
import { Subject } from 'rxjs';

@WithPageMeta({ pageTitle: 'bookATimeslot' })
@WithDynamicContent({ target: '/shop/content/bookatimeslot' })
@Component({
	selector: 'wnz-how-where-when',
	templateUrl: './how-where-when.page.html',
	styleUrls: ['./how-where-when.page.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [
		NgIf,
		FormsModule,
		FormComponent,
		ReactiveFormsModule,
		FunnelBreadcrumbsComponent,
		FulfilmentMethodComponent,
		DeliverySaverTagComponent,
		FieldsetComponent,
		AlertComponent,
		ButtonComponent,
		SvgIconComponent,
		FulfilmentDateSelectionComponent,
		FulfilmentTimeSelectionComponent,
		NgClass,
		RouterOutlet,
		CurrencyPipe,
		FulfilmentFlexibleDeliverySlotSelectionComponent,
	],
})
export class HowWhereWhenPage implements OnInit, OnDestroy {
	address: string;
	currentDate: string;
	fulfilmentTimeSlotForm: FormGroup;
	hasActiveDeliverySubscription: boolean;
	inDeliveryZone = true;
	reducedRangeAlert: Alert | undefined;
	someAvailability: boolean;
	isBasketEmpty: boolean;
	isLoggedInShopper: boolean;
	isInCheckout = false;
	showContinueButton: boolean;
	continueButtonState = ButtonState.default;
	keepShoppingButtonState = ButtonState.default;
	eligibilityForDeliverySubscriptionDiscount: BasketTotalsResponse.EligibilityForDeliverySubscriptionDiscountEnum;
	eligibilityForDeliverySubscriptionDiscountEnum =
		BasketTotalsResponse.EligibilityForDeliverySubscriptionDiscountEnum;
	deliverySubscriptionAlert: Alert | undefined;
	isExpressPickupEnabled = false;
	isExpressDeliveryEnabled = false;
	expressFulfilmentSettings: ExpressFulfilmentSettingsResponse;
	isUserSelectedDateToday: boolean;
	expressSlotStatus?: AvailabilityStatusEnum;
	isSameDayDeliveryEnabled = false;
	enableDTBOptInOutWithGroupedOfferrings = false;
	isPickupSlotOfferingsFeatureEnabled = false;
	standardDeliveryFeeMinimum?: number;
	standardDeliveryFeeMaximum?: number;
	sameDayDeliveryFeeMinimum?: number;
	sameDayDeliveryFeeMaximum?: number;
	isExpressPickupSlotOfferingsFeatureEnabled: boolean;
	private destroyed$: Subject<boolean> = new Subject();
	expressTotalQuantityToRemove?: number;
	isRebrandToDeliveryNowEnabled = false;
	maxItemQuantityTotalAllowedForExpress: number | undefined;
	itemsOfFlexibleDeliverySlot: number | undefined;
	maxItemQuantityTotalAllowedForFlexibleDelivery: number | undefined;
	isUseItemsLimitFromFulfilmentSlotsEnabled = false;
	isFlexibleDeliveryEnabled = false;
	isFlexibleDeliveryEnabledV2 = false;
	isFlexibleSlotAvailable = false;
	selectedFlexibleSlot?: FulfilmentWindowSummarySlot;
	selectedSlot: string;

	constructor(
		private fulfilmentService: FulfilmentService,
		private fb: FormBuilder,
		private fulfilmentStoreService: FulfilmentStoreService,
		private loggingService: LoggingService,
		private shopperService: ShopperService,
		private router: Router,
		private historyService: HistoryService,
		private modalRoutingService: ModalRoutingService,
		private cdr: ChangeDetectorRef,
		private basketService: BasketService,
		private featureService: FeatureService,
		private appSettingsService: AppSettingsService,
		private medalliaSurveyDataPushService: MedalliaSurveyDataPushService,
		private tealiumUtagService: TealiumUtagService
	) {
		router.events
			.pipe(
				takeUntil(this.destroyed$),
				filter(
					(routerEvent) =>
						routerEvent instanceof ActivationEnd &&
						!this.modalRoutingService.isInModal(MODAL_ROUTES.MODAL_OUTLET, routerEvent.snapshot)
				)
			)
			.subscribe((routerEvent) => {
				this.checkRouterEvent(<ActivationEnd>routerEvent);
			});
	}

	get showExpressSlot(): boolean {
		if (
			!this.expressSlotStatus ||
			!this.isUserSelectedDateToday ||
			(this.fulfilmentTimeSlotForm.controls['method'].value === 'Pickup' && !this.isExpressPickupEnabled) ||
			(this.fulfilmentTimeSlotForm.controls['method'].value === 'Delivery' && !this.isExpressDeliveryEnabled) ||
			this.expressSlotStatus === AvailabilityStatusEnum.StoreDoesNotSupportExpress
		) {
			return false;
		}
		return true;
	}

	get showFlexibleSlot(): boolean {
		return (
			this.getFormValueOrDefault('method') === 'Delivery' &&
			this.isExpressDeliveryEnabled &&
			this.isFlexibleDeliveryEnabled &&
			this.isFlexibleSlotAvailable &&
			this.isUserSelectedDateToday
		);
	}

	get multipleFlexibleSlotsAvailable(): boolean {
		return (this.itemsOfFlexibleDeliverySlot ?? 0) > 1;
	}

	get addressLabel(): string {
		return this.getFormValueOrDefault('method') === 'Delivery' ? 'Deliver to:' : 'Pick up from:';
	}

	get addressLink(): string {
		return this.getFormValueOrDefault('method') === 'Delivery' ? 'Change address' : 'Change store';
	}

	ngOnDestroy(): void {
		this.destroyed$.next(true);
		this.destroyed$.complete();
	}

	getSelectedFlexibleSlot(): FulfilmentWindowSummarySlot | undefined {
		return this.fulfilmentService.getFlexibleSelectedSlot();
	}

	ngOnInit(): void {
		this.shopperService.state$.pipe(takeUntil(this.destroyed$)).subscribe((r: ShopperState) => {
			this.hasActiveDeliverySubscription = r.hasActiveDeliverySubscription || false;
			this.isLoggedInShopper = (r.isLoggedIn && r.isShopper) || false;
			this.updateShowContinueButton();
		});

		this.fulfilmentStoreService
			.select('slots')
			.pipe(takeUntil(this.destroyed$), distinctUntilChanged())
			.subscribe((slots: FulfilmentWindowSummarySlot[] | undefined) => {
				this.maxItemQuantityTotalAllowedForExpress = slots?.find((slot) => slot.isExpress)?.itemsLimit;
				this.itemsOfFlexibleDeliverySlot = slots?.filter((slot) => slot.isFlexibleDelivery).length;
				this.maxItemQuantityTotalAllowedForFlexibleDelivery = slots?.find(
					(slot) => slot.isFlexibleDelivery
				)?.itemsLimit;
			});

		this.basketService.state$.pipe(takeUntil(this.destroyed$)).subscribe((r: BasketState) => {
			this.eligibilityForDeliverySubscriptionDiscount =
				r.eligibilityForDeliverySubscriptionDiscount ||
				BasketTotalsResponse.EligibilityForDeliverySubscriptionDiscountEnum.NoSubscription;

			//  Basket Express improvements
			const itemQuantinty = r.totalItemQuantity || 0;
			const maxItemQuantityTotalAllowedForExpress = this.basketService.getMaxAlowedItemQuantityForExpressOrder();
			if (
				maxItemQuantityTotalAllowedForExpress &&
				itemQuantinty > <number>maxItemQuantityTotalAllowedForExpress
			) {
				this.expressTotalQuantityToRemove = itemQuantinty - <number>maxItemQuantityTotalAllowedForExpress;
			}
		});

		this.featureService.state$.pipe(takeUntil(this.destroyed$)).subscribe(() => {
			this.isExpressPickupEnabled = this.featureService.isFeatureEnabled(
				ContextResponse.EnabledFeaturesEnum.ExpressPickup
			);
			this.isExpressDeliveryEnabled = this.featureService.isFeatureEnabled(
				ContextResponse.EnabledFeaturesEnum.ExpressDelivery
			);
			this.isSameDayDeliveryEnabled = this.featureService.isFeatureEnabled(
				ContextResponse.EnabledFeaturesEnum.SameDayDelivery
			);
			this.isPickupSlotOfferingsFeatureEnabled = this.featureService.isFeatureEnabled(
				ContextResponse.EnabledFeaturesEnum.PickupSlotOfferings
			);

			this.isExpressPickupSlotOfferingsFeatureEnabled = this.featureService.isFeatureEnabled(
				ContextResponse.EnabledFeaturesEnum.ExpressPickupSlotOfferings
			);

			this.isRebrandToDeliveryNowEnabled = this.featureService.isFeatureEnabled(
				ContextResponse.EnabledFeaturesEnum.EnableRebrandToDeliveryNow
			);

			this.isUseItemsLimitFromFulfilmentSlotsEnabled = this.featureService.isFeatureEnabled(
				ContextResponse.EnabledFeaturesEnum.UseItemsLimitFromFulfilmentSlots
			);

			this.isFlexibleDeliveryEnabled = this.featureService.isFeatureEnabled(
				ContextResponse.EnabledFeaturesEnum.EnableFlexibleDelivery
			);

			this.isFlexibleDeliveryEnabledV2 = this.featureService.isFeatureEnabled(
				ContextResponse.EnabledFeaturesEnum.EnableFlexibleDeliveryV2
			);

			this.enableDTBOptInOutWithGroupedOfferrings = this.featureService.isFeatureEnabled(
				ContextResponse.EnabledFeaturesEnum.EnableDTBOptInOutWithGroupedOfferrings
			);

			this.cdr.markForCheck();
		});

		this.appSettingsService.state$.pipe(takeUntil(this.destroyed$)).subscribe((appSettingsState) => {
			this.expressFulfilmentSettings = appSettingsState.expressFulfilmentSettings;
		});

		this.isBasketEmpty = this.basketService.isBasketEmpty();
		this.updateShowContinueButton();

		this.initFormStateSubscription();

		this.fulfilmentService.getTimeslots();

		this.trackPageLoad();
	}

	trackSlotReservationSuccess(isContinue: boolean): void {
		const pageData = isContinue
			? { ...cartFlowFulfilmentSubmitted, ...cartFlowCommon }
			: { ...fulfilmentChangeCompleted, ...fulfilmentChangeCommon };

		this.tealiumUtagService.link({
			...pageData,
			...this.fulfilmentService.getAnalyticsData(),
			...this.basketService.getAnalyticsData(),
		});

		this.fulfilmentService.trackFulfilmentChangeInDatalayer(this.fulfilmentStoreService.state);
	}

	trackPageLoad(): void {
		const pageData = this.isInCheckout
			? { ...cartFlowCheckoutInitiated, ...cartFlowCommon }
			: { ...fulfilmentChangeLoadTimeslots, ...fulfilmentChangeCommon };

		this.tealiumUtagService.link({
			...pageData,
			...this.basketService.getAnalyticsData(),
		});
	}

	initFormStateSubscription(): void {
		this.fulfilmentTimeSlotForm = this.fb.group({
			method: this.fulfilmentService.mappedToClientMethod(this.fulfilmentStoreService.state.method || 'Courier'),
			day: '',
			slot: ['', Validators.required],
		});

		this.initFormValueChangeListeners();

		this.initStateSubscriptions();
	}

	initFormValueChangeListeners(): void {
		this.fulfilmentTimeSlotForm.controls['slot'].valueChanges
			.pipe(takeUntil(this.destroyed$), distinctUntilChanged(), map(this.fulfilmentService.mappedToServerMethod))
			.subscribe((slot: string) => {
				this.selectedSlot = slot;
			});
		this.fulfilmentTimeSlotForm.controls['method'].valueChanges
			.pipe(takeUntil(this.destroyed$), distinctUntilChanged(), map(this.fulfilmentService.mappedToServerMethod))
			.subscribe((method: 'Pickup' | 'Courier') =>
				this.fulfilmentService.changeFulfilmentMethod(method).subscribe()
			);

		this.fulfilmentTimeSlotForm.controls['day'].valueChanges
			.pipe(takeUntil(this.destroyed$), distinctUntilChanged())
			.subscribe((value: string) => {
				this.currentDate = value;
				this.isUserSelectedDateToday = dayjs().isSame(dayjs(this.currentDate), 'day');
				this.fulfilmentStoreService.setState({
					clientSelectedDate: value,
				});
				this.cdr.markForCheck();
			});
	}

	initStateSubscriptions(): void {
		this.fulfilmentStoreService.state$
			.pipe(takeUntil(this.destroyed$))
			.subscribe((fulfilmentState: FulfilmentState) => {
				this.address = fulfilmentState.address || '';
				this.inDeliveryZone = Boolean(fulfilmentState.isAddressInDeliveryZone);
				this.expressSlotStatus = fulfilmentState.expressSlotStatus;
				this.isFlexibleSlotAvailable = fulfilmentState.flexibleDeliverySlotAvailable || false;
				this.reducedRangeAlert = this.fulfilmentService.getReducedRangeAlert();
				this.deliverySubscriptionAlert = this.fulfilmentService.getDeliverySubscriptionAlert(
					this.eligibilityForDeliverySubscriptionDiscount
				);
				this.sameDayDeliveryFeeMinimum = fulfilmentState.sameDayDeliveryFeeMinimum;
				this.sameDayDeliveryFeeMaximum = fulfilmentState.sameDayDeliveryFeeMaximum;
				this.standardDeliveryFeeMinimum = fulfilmentState.standardDeliveryFeeMinimum;
				this.standardDeliveryFeeMaximum = fulfilmentState.standardDeliveryFeeMaximum;

				this.cdr.markForCheck();
			});

		// only need to listen once for the initial method and any selected day
		this.fulfilmentStoreService.state$
			.pipe(
				takeUntil(this.destroyed$),
				filter((s) => s.updatedFromServer || false),
				take(1),
				distinctUntilChanged((a, b) => equal(a, b)),
				map((s: FulfilmentState) => {
					if (s.selectedDateWithTZInfo) {
						let realDate = dayjs(s.selectedDateWithTZInfo);

						// Trader backend enabled CO9801_RemoveTimeComponentOfDeliveryWindowReservation to remove the time component of the delivery window reservation.
						// Check if the hour is 0, then add 2 hours. This is to match selected date with the day slot value.
						// See line 404 in fulfilment.service.ts
						if (realDate.hour() === 0) {
							realDate = realDate.add(2, 'hour');
						}
						s.selectedDate = realDate.format();
					}
					return s;
				})
			)
			.subscribe((fulfilmentState: FulfilmentState) => {
				this.patchMethodAndDateFormValues(fulfilmentState);
			});
	}

	patchMethodAndDateFormValues(fulfilmentState: FulfilmentState): void {
		// patch form with whatever fulfilment state says our method is
		const mappedMethod = this.fulfilmentService.mappedToClientMethod(fulfilmentState.method || 'Courier');
		if (this.fulfilmentTimeSlotForm.controls['method'].value !== mappedMethod) {
			this.fulfilmentTimeSlotForm.patchValue({
				method: mappedMethod,
			});
		}

		// patch form with selected date from server (note from here on we use clientSelectedDate up until the post back to the server)
		const storedSelectedDateDifferentToFormSelectedDate =
			this.fulfilmentTimeSlotForm.controls['day'].value !== fulfilmentState.selectedDate &&
			fulfilmentState.selectedDate;

		if (storedSelectedDateDifferentToFormSelectedDate) {
			this.fulfilmentTimeSlotForm.patchValue({
				day: fulfilmentState.selectedDate,
			});
		}
	}

	getMethodDisplayText(method: string): string {
		if (method === 'Pickup') {
			return 'Pick up';
		}
		return method;
	}

	getFormValueOrDefault(controlName: string, defaultString = ''): string {
		return this.fulfilmentTimeSlotForm?.controls[controlName]?.value ?? defaultString;
	}

	getCurrentFulfilmentMethod(): string {
		return this.getFormValueOrDefault('method');
	}

	getFlexibleFulfilmentFee(): number | undefined {
		return this.expressFulfilmentSettings?.flexibleDeliveryFee;
	}

	getExpressFulfilmentFee(): number | undefined {
		return this.getCurrentFulfilmentMethod() === 'Pickup'
			? this.expressFulfilmentSettings?.expressPickUpFee
			: this.expressFulfilmentSettings?.expressDeliveryFee;
	}

	getExpressFulfilmentMaxItems(): number | undefined {
		return this.isUseItemsLimitFromFulfilmentSlotsEnabled
			? this.maxItemQuantityTotalAllowedForExpress
			: this.expressFulfilmentSettings?.maxItemQuantityForExpressOrders;
	}

	getFlexibleDeliveryMaxItems(): number | undefined {
		return this.maxItemQuantityTotalAllowedForFlexibleDelivery;
	}

	getFulfilmentMethodLabel(): string {
		return this.getCurrentFulfilmentMethod() === 'Pickup' ? 'Pick up' : 'Delivery';
	}

	getExpressFulfilmentMethodLabel(): string {
		return this.getCurrentFulfilmentMethod() === 'Pickup' ? 'Express Pick up' : this.getExpressDeliveryLabel();
	}

	getFlexibleFulfilmentMethodLabel(): string {
		return '1hr window';
	}

	getExpressDeliveryLabel(): string {
		return this.isRebrandToDeliveryNowEnabled ? 'Delivery Now' : 'Express Delivery';
	}

	getTimeSlotsHeadingText(): string {
		if (
			this.getCurrentFulfilmentMethod() === 'Delivery' &&
			this.isSameDayDeliveryEnabled &&
			this.isUserSelectedDateToday
		) {
			return 'Same Day ' + this.getCurrentFulfilmentMethod();
		} else {
			return 'Standard ' + this.getMethodDisplayText(this.getCurrentFulfilmentMethod());
		}
	}

	getTimeSlotsHeadingPricingText(): string {
		if (!this.isSameDayDeliveryEnabled || this.getCurrentFulfilmentMethod() !== 'Delivery') {
			return '';
		}
		if (
			this.expressFulfilmentSettings === null ||
			this.eligibilityForDeliverySubscriptionDiscount ===
				BasketTotalsResponse.EligibilityForDeliverySubscriptionDiscountEnum.Eligible
		) {
			return '';
		}
		if (
			this.standardDeliveryFeeMinimum === undefined ||
			this.standardDeliveryFeeMaximum === undefined ||
			this.sameDayDeliveryFeeMinimum === undefined ||
			this.sameDayDeliveryFeeMaximum === undefined
		) {
			return '';
		}

		if (this.isUserSelectedDateToday) {
			if (this.sameDayDeliveryFeeMinimum === this.sameDayDeliveryFeeMaximum) {
				return `- $${this.sameDayDeliveryFeeMinimum}`;
			}
			return `- $${this.sameDayDeliveryFeeMinimum} or $${this.sameDayDeliveryFeeMaximum}`;
		}
		if (this.standardDeliveryFeeMinimum === this.standardDeliveryFeeMaximum) {
			return `- $${this.standardDeliveryFeeMinimum}`;
		}
		return `- $${this.standardDeliveryFeeMinimum} or $${this.standardDeliveryFeeMaximum}`;
	}

	openAddressOrStoreSelection(): void {
		const modalAddress =
			this.getCurrentFulfilmentMethod() === 'Delivery'
				? MODAL_ROUTES.CHANGE_DELIVERY_ADDRESS_PATH
				: MODAL_ROUTES.CHANGE_PICK_UP_STORE_PATH;
		this.modalRoutingService.open(MODAL_ROUTES.HWW_MODAL, modalAddress);
	}

	openFlexibleSlotSelection(): void {
		this.modalRoutingService.open(MODAL_ROUTES.HWW_MODAL, MODAL_ROUTES.CHANGE_FLEXIBLE_SLOT_PATH);
	}

	openDeliveryFeesModal(isExpress: boolean): void {
		this.modalRoutingService.open(
			MODAL_ROUTES.HWW_MODAL,
			isExpress ? MODAL_ROUTES.EXPRESS_DELIVERY_FEES_PATH : MODAL_ROUTES.DELIVERY_FEES_PATH
		);
	}

	openFlexibleDeliveryFeesModal(): void {
		this.modalRoutingService.open(MODAL_ROUTES.HWW_MODAL, MODAL_ROUTES.FLEXIBLE_DELIVERY_FEES_PATH);
	}

	handleKeepShoppingClick(form: FormComponent): void {
		if (form.canSubmit) {
			this.reserveSlot(false);
		} else {
			this.navigateToPreviousPage();
		}
	}

	getSlotOffering(slotId: number, pickupType: string): SlotsOffering | undefined {
		const offerings = this.fulfilmentStoreService.state.slots?.find((x) => x.id === slotId)?.offerings || [];
		for (const offering of offerings) {
			// Check if the offering itself matches the target value
			if (offering.value === pickupType) {
				return offering;
			}

			// Check within groupedOfferings if it exists
			if (offering.groupedOfferings) {
				const found = offering.groupedOfferings.find((o) => o.value === pickupType);
				if (found) {
					return found;
				}
			}
		}
		return undefined;
	}

	reserveSlot(isContinue: boolean): void {
		if (isContinue) {
			this.continueButtonState = ButtonState.busy;
		} else {
			this.keepShoppingButtonState = ButtonState.busy;
		}
		const idParts = this.fulfilmentTimeSlotForm.value.slot.split('~');
		const pickupType = this.fulfilmentTimeSlotForm.value.pickupType;
		const itemsLimit = idParts.length > 2 && idParts[2].length > 0 ? parseInt(idParts[2], 10) : undefined;
		const slotId = parseInt(idParts[1], 10);

		const selectedOffering = this.getSlotOffering(slotId, pickupType);
		this.fulfilmentStoreService.setState({
			pickupType,
			itemsLimit,
		});

		const trolleyMetadata: UpdateTrolleyMetadataRequest = {
			pickupType: undefined,
			itemsLimit: undefined,
			isReverseParkingRequired: undefined,
		};

		if (this.isPickupSlotOfferingsFeatureEnabled) {
			trolleyMetadata.pickupType = <MaybeString>(<unknown>this.fulfilmentTimeSlotForm.value.pickupType) || '';
		}
		if (this.isUseItemsLimitFromFulfilmentSlotsEnabled) {
			trolleyMetadata.itemsLimit = itemsLimit ?? 0;
		}

		if (this.enableDTBOptInOutWithGroupedOfferrings) {
			trolleyMetadata.isReverseParkingRequired = <OptionalMaybeBoolean>(
				(<unknown>selectedOffering?.attributes?.isReverseParkingRequired)
			);
		}

		if (Object.keys(trolleyMetadata).length > 0) {
			this.basketService.updateTrolleyMetadata(trolleyMetadata).pipe(takeUntil(this.destroyed$)).subscribe();
		}

		this.fulfilmentService
			.changeTimeslot({
				windowId: slotId,
				windowDate: idParts[0],
				isLockerPickup: pickupType !== null ? pickupType === PickupType.Locker : undefined,
			})
			.pipe(takeUntil(this.destroyed$))
			.subscribe(
				(r: any) => this.onSlotReservationSuccess(r, isContinue),
				(e: any) => this.onSlotReservationError(e, isContinue),
				() => {
					this.continueButtonState = ButtonState.default;
					this.keepShoppingButtonState = ButtonState.default;
				}
			);
	}

	onSlotReservationSuccess = (r: any, isContinue: boolean): void => {
		this.continueButtonState = ButtonState.default;
		this.keepShoppingButtonState = ButtonState.default;

		if (r.isSuccessful) {
			this.trackSlotReservationSuccess(isContinue);

			this.basketService.expressTotalQuantityToRemove = this.expressTotalQuantityToRemove;

			this.medalliaSurveyDataPushService.trackExpressAbandonedEvent(
				this.fulfilmentStoreService.state.isExpressAbandoned || false
			);
			if (isContinue) {
				if (this.isInCheckout) {
					this.router.navigateByUrl(REVIEW_ORDER_PATH);
				} else {
					this.router.navigateByUrl(HAVE_YOU_FORGOTTEN_PATH);
				}
			} else {
				this.navigateToPreviousPage();
			}
		}
	};

	onSlotReservationError = (r: any, isContinue: boolean): void => {
		if (isContinue) {
			this.continueButtonState = ButtonState.errored;
		} else {
			this.keepShoppingButtonState = ButtonState.errored;
		}
		// errors handled by service and modals - just stay where we are
		this.loggingService.trackException({ error: new Error(JSON.stringify(r)) });
	};

	updateShowContinueButton(): void {
		this.showContinueButton = !this.isBasketEmpty && this.isLoggedInShopper;
		this.cdr.markForCheck();
	}

	openPickupHelpModal(): void {
		this.modalRoutingService.open(MODAL_ROUTES.HWW_MODAL, MODAL_ROUTES.PICKUP_INFORMATION);
	}

	getDirectToBootMessage(): string {
		const countdown = 'Countdown';
		const indexOfCountdown = this.address.indexOf(countdown);
		return (
			this.address.substring(indexOfCountdown + countdown.length).trimStart() + ' offers Direct to Boot pick up.'
		);
	}

	openDirectToBootLearnMoreModal(): void {
		this.modalRoutingService.open(MODAL_ROUTES.HWW_MODAL, MODAL_ROUTES.DIRECT_TO_BOOT_MODAL_PATH);
	}

	openPickupPointInformationModal(): void {
		this.modalRoutingService.open(MODAL_ROUTES.HWW_MODAL, MODAL_ROUTES.PICKUP_POINT_INFORMATION_MODAL_PATH);
	}

	openDualOfferInformationModal(): void {
		this.modalRoutingService.open(MODAL_ROUTES.HWW_MODAL, MODAL_ROUTES.DUAL_OFFER_MODAL_PATH);
	}

	openDirectToBootOnlyInformationModal(): void {
		this.modalRoutingService.open(MODAL_ROUTES.HWW_MODAL, MODAL_ROUTES.DIRECT_TO_BOOT_GROUPED_MODAL_PATH);
	}

	showStandardPickup(): boolean {
		return !(
			this.getCurrentFulfilmentMethod() === 'Pickup' &&
			this.isPickupSlotOfferingsFeatureEnabled &&
			this.isExpressPickupSlotOfferingsFeatureEnabled &&
			this.showExpressSlot
		);
	}

	private checkRouterEvent(routerEvent: ActivationEnd): void {
		this.isInCheckout = checkFullActivationEndForData(routerEvent, 'isCheckout');
	}

	private navigateToPreviousPage(): void {
		let prevUrl = this.historyService.getPreviousNonModalUrlForOutlet(MODAL_ROUTES.HWW_MODAL, [
			this.router.url,
			`/shop/${REGISTRATION_SUCCESSFUL_PATH}`,
			`/shop/${REGISTRATION_SHOPPER_DETAILS_PATH}`,
			`/shop/${REGISTRATION_PATH}`,
			`/${ORDER_CONFIRMATION_PATH}`,
			`/${EXPRESS_LANDING_PAGE_PATH}`,
		]);

		// If the user was on the PDP page from a Google Merchant Hosted Local Storefront store, then update the store
		// to the  newly selected store
		if (prevUrl.toLowerCase().indexOf('store=') !== -1) {
			const parsedUrl = this.router.parseUrl(prevUrl);

			parsedUrl.queryParams = {
				stockcode: parsedUrl.queryParams.stockcode,
				name: parsedUrl.queryParams.name,
				ostore: this.fulfilmentService.fulfilmentStoreId,
			};

			prevUrl = this.router.serializeUrl(parsedUrl);
		}

		this.router.navigateByUrl(prevUrl);
	}
}
